import { motion } from 'framer-motion';
import { Shield, Heart, Award, Clock, MapPin, Phone } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_kidzone-bookings/artifacts/oxkzqne6_logo_white_background.jpg";

export const AboutSection = () => {
  const { t } = useLanguage();

  const features = [
    {
      icon: Shield,
      title: t('about.features.safe.title'),
      description: t('about.features.safe.description')
    },
    {
      icon: Heart,
      title: t('about.features.love.title'),
      description: t('about.features.love.description')
    },
    {
      icon: Award,
      title: t('about.features.quality.title'),
      description: t('about.features.quality.description')
    },
    {
      icon: Clock,
      title: t('about.features.flexible.title'),
      description: t('about.features.flexible.description')
    }
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-brand-orange/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-0 w-72 h-72 bg-brand-blue/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <span className="inline-block px-4 py-2 rounded-full bg-brand-pink/10 text-brand-pink font-heading font-bold text-sm mb-4">
                {t('about.badge')}
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {t('about.title1')}{' '}
                <span className="gradient-text">{t('about.titleHighlight')}</span>{' '}
                {t('about.title2')}
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed">
                {t('about.description')}
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex gap-4"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-brand-orange/10 flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-brand-orange" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-gray-900 mb-1">{feature.title}</h4>
                    <p className="font-body text-sm text-gray-600">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Quick Info */}
            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-brand-blue/10">
                  <MapPin className="w-5 h-5 text-brand-blue" />
                </div>
                <div>
                  <p className="font-body text-xs text-gray-500">{t('about.location')}</p>
                  <p className="font-heading font-bold text-gray-800">Yaounde, Cameroon</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-brand-pink/10">
                  <Phone className="w-5 h-5 text-brand-pink" />
                </div>
                <div>
                  <p className="font-body text-xs text-gray-500">{t('about.callUs')}</p>
                  <p className="font-heading font-bold text-gray-800">695521384</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Image/Logo Composition */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_kidzone-bookings/artifacts/n8yedh96_IMG_20230223_140614_123.jpg"
                  alt="Kids playing at Mascot&Candy"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-orange/30 to-transparent" />
              </div>

              {/* Logo Badge */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3, type: 'spring' }}
                className="absolute -bottom-8 -left-8 w-40 h-40 bg-white rounded-3xl shadow-2xl p-4 flex items-center justify-center"
              >
                <img 
                  src={LOGO_URL} 
                  alt="Mascot&Candy Logo"
                  className="w-full h-full object-contain"
                />
              </motion.div>

              {/* Stats Badge */}
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4, type: 'spring' }}
                className="absolute -top-6 -right-6 bg-brand-orange text-white rounded-2xl shadow-xl p-6 text-center"
              >
                <p className="font-heading text-4xl font-bold">5K+</p>
                <p className="font-body text-sm opacity-90">{t('about.happyKids')}</p>
              </motion.div>

              {/* Decorative Blob */}
              <div className="absolute -z-10 top-10 -right-10 w-48 h-48 bg-brand-pink/20 rounded-full blur-2xl" />
              <div className="absolute -z-10 -bottom-10 right-20 w-32 h-32 bg-brand-blue/20 rounded-full blur-2xl" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
